/**
 * studio-app-lazy-editors — the shared `React.lazy` editor builder for the
 * generated studio clusters (#705 S2), extracted from `studio-app-plugin.tsx`.
 *
 * Both the FULL `studioAppPlugin(model)` and the schema-free
 * `studioAppRoutePlugin(descriptor)` resolve a cluster's generated editors the
 * SAME way: a memoised dynamic `import('./studio-app-editors.js')` (the heavy
 * render + runtime chunk) resolves the transport wrapper by discriminant and
 * calls `buildStudioAppEditors`, and a `React.lazy` wrapper per editor id picks
 * its component out of that ONE shared map. Extracting it here means the two
 * plugins share ONE builder — the full plugin captures the model EAGERLY (it is
 * already on the entry with `generate()`), the route plugin supplies a LAZY model
 * loader (a dynamic-`import()` thunk) so the model schema stays off the entry.
 *
 * ENTRY-SAFE: this module statically imports only React's `lazy` (already on the
 * entry) and TYPES (erased). The heavy `./studio-app-editors.js` chunk + the
 * `DomainModel` are pulled ONLY inside the dynamic `import()` / lazy-loader
 * thunks — never onto the studio entry.
 *
 * @module @ubiclouds/v5-studio/genome/studio-app-lazy-editors
 */

import { lazy, type ComponentType } from 'react';
import type { DomainModel } from '@ubiclouds/v5-genome-gen';
import type { AppTransportWrapper } from './studio-app-editors.js';
import type { StudioAppTransport } from './studio-app-route-placement.js';

/** The deferred editor chunk's module shape (structural type — compile-erased). */
type EditorsModule = typeof import('./studio-app-editors.js');

/** The generated editor ids for a cluster: the app `index` + one `{node}-view` per node. */
export function editorIdsForNodes(nodeNames: readonly string[]): string[] {
  return ['index', ...nodeNames.map((name) => `${name}-view`)];
}

/**
 * Resolve the per-transport wrapper INSIDE the deferred chunk (#S-18b/#363a) so
 * the live-seam / localStorage-seam modules never enter the studio entry.
 */
function resolveTransportWrapper(m: EditorsModule, transport: StudioAppTransport): AppTransportWrapper {
  switch (transport) {
    case 'host-app-live':
      return m.hostAppLiveTransportWrapper;
    case 'agent-profiles-local':
      return m.agentProfilesLocalTransportWrapper;
    case 'slicer-profile-local':
      return m.slicerProfileLocalTransportWrapper;
    case 'filament-local':
      return m.filamentLocalTransportWrapper;
    case 'monzo-fixture':
      return m.monzoFixtureTransportWrapper;
    case 'home-battery-local':
      return m.hbLocalTransportWrapper;
    default:
      return m.studioMockTransportWrapper;
  }
}

/**
 * Build a memoised loader of a cluster's editor map. The dynamic `import()` is
 * cached so every lazy editor wrapper resolves the SAME built editor map (one
 * chunk fetch, one runtime + one event-bus instance), matching the prior single
 * eager construction.
 *
 * `loadModel` returns the cluster's `DomainModel` — EAGER (`() => model`) for the
 * full plugin, or a LAZY dynamic-`import()` thunk for the route-only plugin so
 * the model schema stays off the entry. `routePrefix` threads the studio's route
 * placement (#478) so the generated index links land on the registered routes.
 */
export function makeEditorMapLoader(
  loadModel: () => DomainModel | Promise<DomainModel>,
  transport: StudioAppTransport,
  routePrefix?: string,
): () => Promise<Record<string, ComponentType>> {
  let editorMapPromise: Promise<Record<string, ComponentType>> | undefined;
  return () => {
    editorMapPromise ??= import('./studio-app-editors.js').then(async (m) => {
      const model = await loadModel();
      return m.buildStudioAppEditors(model, resolveTransportWrapper(m, transport), routePrefix);
    });
    return editorMapPromise;
  };
}

/**
 * Build the `React.lazy` editor wrappers keyed by editor id. Each wrapper resolves
 * the shared editor map and picks its own component out by id; the App renders the
 * matched editor under its existing Suspense boundary, so the first navigation to
 * a generated route loads the chunk transparently.
 */
export function buildLazyEditors(
  editorIds: readonly string[],
  loadEditorMap: () => Promise<Record<string, ComponentType>>,
): Record<string, ComponentType> {
  const editors: Record<string, ComponentType> = {};
  for (const editorId of editorIds) {
    editors[editorId] = lazy(() =>
      loadEditorMap().then((map) => {
        const Editor = map[editorId];
        if (!Editor) {
          throw new Error(`studio-app-plugin: generated editor '${editorId}' not found`);
        }
        return { default: Editor };
      }),
    );
  }
  return editors;
}
